<template>
  <div v-if="pageInfo" class="page-info-panel">
    <button
      type="button"
      class="page-info-panel__toggle"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span class="page-info-panel__toggle-icon">
        <i class="fa-regular fa-circle-question" />
      </span>
      <span class="page-info-panel__toggle-text" v-text="t('admin_global_pageinfo_title')" />
      <i
        class="page-info-panel__chevron fa-solid"
        :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"
      />
    </button>

    <div v-show="expanded" class="page-info-panel__body">
      <div v-if="pageInfo.what" class="page-info-panel__section">
        <p class="page-info-panel__label" v-text="t('admin_global_pageinfo_what')" />
        <p class="page-info-panel__text" v-text="pageInfo.what" />
      </div>
      <div v-if="pageInfo.why" class="page-info-panel__section">
        <p class="page-info-panel__label" v-text="t('admin_global_pageinfo_why')" />
        <p class="page-info-panel__text" v-text="pageInfo.why" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IPageInfoContent } from '@/types/PageInfo'
  import { UseI18n } from '@/utils-ts'
  import { getPageInfo } from '@/utils-ts/pageInfo'
  import { computed, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const { t, te } = UseI18n()

  const expanded = ref(false)

  const pageInfo = computed<IPageInfoContent | null>(() => {
    if (route.meta.pageInfo === false) return null
    return getPageInfo(route.meta.pageResource as string | null, t, te)
  })

  watch(
    () => route.fullPath,
    () => {
      expanded.value = false
    },
  )
</script>

<style lang="scss" scoped>
  .page-info-panel {
    margin-bottom: 1rem;
    border: 1px solid rgba(var(--brand-color-rgb), 0.2);
    border-radius: 8px;
    background: rgba(var(--brand-color-rgb), 0.04);
    overflow: hidden;

    &__toggle {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.75rem 1rem;
      border: 0;
      background: transparent;
      cursor: pointer;
      text-align: left;
      color: $primary-text-color;
      font-size: 0.875rem;
      font-weight: 600;

      &:hover {
        background: rgba(var(--brand-color-rgb), 0.06);
      }
    }

    &__toggle-icon {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      background: rgba(var(--brand-color-rgb), 0.12);
      color: var(--brand-color);
      @include flexible($justify: center, $align: center);
      flex-shrink: 0;
    }

    &__toggle-text {
      flex: 1;
    }

    &__chevron {
      color: var(--brand-color);
      font-size: 0.75rem;
    }

    &__body {
      padding: 0 1rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
    }

    &__section {
      padding: 0.875rem 1rem;
      border-radius: 6px;
      background: $white;
      border: 1px solid #ececec;
    }

    &__label {
      margin: 0 0 0.375rem;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--brand-color);
    }

    &__text {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: $primary-text-color;
      white-space: pre-line;
    }
  }
</style>
